// Grazer archetype (MOB-2): passive animals — wander, flee when hurt, breed on
// their food item, and grow babies (MOB-2 baby scale 0.5). PURE (ENG-1).
import { BREED, HER, MOB } from '../../config';
import type { MobDef } from '../../registry/schemas';
import { HURT_TICKS, type Entity, type MobContext } from '../entity';
import { wander, moveToward, moveAway, tickSpeed } from './shared';
import { isEquine, breedsWith, equineOffspring } from '../../game/equine';
import { SALT_HER_BREED } from '../../engine/rng';
import { recombine } from '../../heredity/breed';
import { paced } from '../../heredity/aging';

/** Distance at which a held food item feeds a nearby animal (MOB-2). */
const FEED_RADIUS = 2.5;
/** How near a player must be for a hurt grazer to flee it (blocks). Under the rule it
 *  scales by `fleeMul` (§28.7). */
const FLEE_RADIUS = 12;
/** How far ahead a wandering grazer aims (blocks) — × `wanderMul` under the rule. */
const WANDER_REACH = 4;

export function grazer(e: Entity, def: MobDef, ctx: MobContext): void {
  const { frame, sys } = ctx;
  // §28.7: temperament applies only to an animal WITH a genome in a world WITH the rule.
  // Everything else takes the classic branches below, unchanged to the draw — the M49.3
  // grazer golden (recorded on pre-Part-IX main) pins that.
  const genetic = e.genome !== undefined && sys.heredityIsOn();

  if (genetic) {
    if (temperamentFlee(e, def, ctx)) return;
  } else if (e.hurtTimer > 0) {
    // Recently hurt → flee the nearest player for a moment.
    const near = sys.nearestPlayer(frame, e.x, e.y, e.z, FLEE_RADIUS);
    if (near) { moveAway(e, ctx, frame.players[near.idx]!.x, frame.players[near.idx]!.z, tickSpeed(def, 1.6)); return; }
  }

  // In love → seek a nearby in-love partner of the same type and breed.
  if (def.breedItem && e.loveTimer > 0 && e.breedCooldown === 0) {
    const partner = findMate(e, ctx);
    if (partner) {
      const d = Math.hypot(partner.x - e.x, partner.z - e.z);
      if (d < 1.4) {
        breed(e, partner, ctx);
        return;
      }
      moveToward(e, ctx, partner.x, partner.z, tickSpeed(def, 1));
      return;
    }
  }

  // A player offering the food item nearby → approach, feed, and fall in love.
  if (def.breedItem && e.loveTimer === 0 && e.breedCooldown === 0) {
    const near = sys.nearestPlayer(frame, e.x, e.y, e.z, 8);
    if (near && frame.players[near.idx]!.offering(def.breedItem)) {
      const pl = frame.players[near.idx]!;
      if (near.dist <= FEED_RADIUS) {
        // §28.8 carrying capacity: under the rule, a table species already at
        // `HER.CAP_COUNT` adults within `HER.CAP_RADIUS` refuses the feed BEFORE the food
        // is taken — the wheat stays, no hearts, and the player is told why. Nothing ever
        // dies of overcrowding; the flock just stops growing here.
        if (def.genes && sys.heredityIsOn()) {
          const adults = sys.nearby(e.x, e.y, e.z, HER.CAP_RADIUS, e.type).filter((o) => !o.baby).length;
          if (adults >= HER.CAP_COUNT) {
            sys.capacityRefused(near.idx, frame.tick ?? 0);
            return;
          }
        }
        pl.consumeOffered(def.breedItem);
        e.loveTimer = timer(BREED.LOVE_TICKS, def, ctx);
        return;
      }
      moveToward(e, ctx, pl.x, pl.z, tickSpeed(def, 1));
      return;
    }
  }

  if (genetic) temperamentWander(e, def, ctx);
  else wander(e, ctx, def);
}

/** §28.7 flee + startle, for a genetic grazer. Returns whether it is fleeing this tick.
 *
 *  A fresh hit (the first tick `hurtTimer` shows it) starts a flee lasting
 *  `HURT_TICKS × fleeMul`, and startles same-species neighbours within
 *  `HER.STARTLE_RADIUS`: each flees too with ITS OWN `startleP` — a skittish animal is one
 *  that startles easily — drawing one `ctx.rng` value only when that `startleP > 0`. While
 *  fleeing it runs from the nearest player within `FLEE_RADIUS × fleeMul`. */
function temperamentFlee(e: Entity, def: MobDef, ctx: MobContext): boolean {
  const { frame, sys } = ctx;
  if (e.hurtTimer === HURT_TICKS - 1) {
    e.fleeTimer = Math.round(HURT_TICKS * e.traits.fleeMul);
    for (const n of sys.nearby(e.x, e.y, e.z, HER.STARTLE_RADIUS, e.type)) {
      if (n.id === e.id || n.baby || n.genome === undefined || n.traits.startleP <= 0) continue;
      if (ctx.rng.nextFloat() < n.traits.startleP) n.fleeTimer = Math.round(HURT_TICKS * n.traits.fleeMul);
    }
  }
  if (!e.fleeTimer) return false;
  e.fleeTimer--;
  const near = sys.nearestPlayer(frame, e.x, e.y, e.z, FLEE_RADIUS * e.traits.fleeMul);
  if (!near) return false;
  moveAway(e, ctx, frame.players[near.idx]!.x, frame.players[near.idx]!.z, tickSpeed(def, 1.6));
  return true;
}

/** §28.7 wander for a genetic grazer: the classic re-roll (the same draws, in the same
 *  order), then — only when `approachP > 0` and a player is within `HER.CURIOUS_RADIUS` —
 *  ONE more draw that, under `approachP`, turns the heading toward that player. The aim
 *  reaches `WANDER_REACH × wanderMul`, and flock cohesion adds its bias only when
 *  `cohesion > 0`, so a zero term costs neither a draw nor a float. */
function temperamentWander(e: Entity, def: MobDef, ctx: MobContext): void {
  const { frame, sys } = ctx;
  const t = e.traits;
  if (e.wanderTimer > 0) {
    e.wanderTimer--;
  } else {
    e.wanderTimer = MOB.WANDER_INTERVAL + ctx.rng.nextInt(MOB.WANDER_INTERVAL);
    e.wanderAngle = ctx.rng.nextFloat() < 0.65 ? ctx.rng.nextFloat() * Math.PI * 2 : Number.NaN;
    if (t.approachP > 0) {
      const near = sys.nearestPlayer(frame, e.x, e.y, e.z, HER.CURIOUS_RADIUS);
      if (near && ctx.rng.nextFloat() < t.approachP) {
        const p = frame.players[near.idx]!;
        e.wanderAngle = Math.atan2(p.z - e.z, p.x - e.x);
      }
    }
  }
  const walk = tickSpeed(def, 0.6);
  // Standing still leaves the steering exactly as the classic wander does.
  if (!Number.isNaN(e.wanderAngle)) {
    const reach = WANDER_REACH * t.wanderMul;
    moveToward(e, ctx, e.x + Math.cos(e.wanderAngle) * reach, e.z + Math.sin(e.wanderAngle) * reach, walk);
  }
  if (t.cohesion > 0) cohere(e, ctx, t.cohesion, walk);
}

/** §28.7 cohesion: bias the steering toward the centroid of same-species adults within
 *  `HER.COHESION_RADIUS`, by `cohesion` of walking speed, then clamp to walking speed. */
function cohere(e: Entity, ctx: MobContext, cohesion: number, walk: number): void {
  let cx = 0, cz = 0, n = 0;
  for (const o of ctx.sys.nearby(e.x, e.y, e.z, HER.COHESION_RADIUS, e.type)) {
    if (o.id === e.id || o.baby) continue;
    cx += o.x; cz += o.z; n++;
  }
  if (n === 0) return;
  const dx = cx / n - e.x, dz = cz / n - e.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return;
  e.wishX += (dx / len) * cohesion * walk;
  e.wishZ += (dz / len) * cohesion * walk;
  const sp = Math.hypot(e.wishX, e.wishZ);
  if (sp > walk) { e.wishX *= walk / sp; e.wishZ *= walk / sp; }
}

function findMate(e: Entity, ctx: MobContext): Entity | null {
  // Same species for everything — except the horse family, where a horse and a
  // donkey are a valid pair (and make a mule). `nearby` filters by type, so the
  // cross-species case has to search unfiltered.
  const pool = isEquine(e.type)
    ? ctx.sys.nearby(e.x, e.y, e.z, BREED.MATE_RADIUS).filter((o) => breedsWith(e.type, o.type))
    : ctx.sys.nearby(e.x, e.y, e.z, BREED.MATE_RADIUS, e.type);
  for (const other of pool) {
    if (other.id === e.id || other.baby) continue;
    if (other.loveTimer > 0 && other.breedCooldown === 0) return other;
  }
  return null;
}

/** Species that lay a distinct offspring instead of a baby of themselves — a frog
 *  breeds a tadpole (which the host grows into a frog, game/frog). */
const OFFSPRING: Record<string, string> = { frog: 'tadpole' };

function breed(a: Entity, b: Entity, ctx: MobContext): void {
  a.loveTimer = 0; b.loveTimer = 0;
  a.breedCooldown = timer(BREED.COOLDOWN_TICKS, ctx.sys.def(a.type)!, ctx);
  b.breedCooldown = timer(BREED.COOLDOWN_TICKS, ctx.sys.def(b.type)!, ctx);
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, mz = (a.z + b.z) / 2;
  // A horse and a donkey make a MULE — the only pairing in the game whose child
  // is neither parent, and the only way a mule is ever born.
  const cross = isEquine(a.type) ? equineOffspring(a.type, b.type) : null;
  const offspring = OFFSPRING[a.type];
  // §28.5: under the rule, the child inherits — its genome recombined from its parents'
  // by the CHILD's table (the horse-family table is one table, so a mule recombines
  // horse × donkey directly), and its parents recorded. Off, `inherit` is empty and every
  // spawn below is exactly the call it always was.
  const inherit = inheritance(a, b, cross ?? offspring ?? a.type, ctx);
  // §28.11: the notebook's auto-record trigger — only for a GENETIC child (a
  // genome was actually inherited), never for a plain birth with the rule off.
  const announceBirth = (child: Entity | null): void => {
    if (child && inherit.genome) ctx.sys.onHeredity?.({ kind: 'her:birth', id: child.id, parents: [a.id, b.id] });
  };
  if (cross) {
    const baby = ctx.sys.spawn(cross, mx, my, mz, { baby: true, ...inherit });
    if (baby) baby.growTimer = timer(BREED.GROW_TICKS, ctx.sys.def(cross)!, ctx);
    announceBirth(baby);
  } else if (offspring) {
    announceBirth(ctx.sys.spawn(offspring, mx, my, mz, inherit)); // e.g. a frog lays a tadpole
  } else {
    const baby = ctx.sys.spawn(a.type, mx, my, mz, { baby: true, ...inherit });
    if (baby) baby.growTimer = timer(BREED.GROW_TICKS, ctx.sys.def(a.type)!, ctx);
    announceBirth(baby);
  }
  // Breeding rewards XP orbs, collectable by the breeder (PLR-5).
  ctx.sys.spawnXp(BREED.XP_MIN + ctx.rng.nextInt(BREED.XP_MAX - BREED.XP_MIN + 1), mx, my, mz);
}

/** A child's inheritance (§28.5), as `spawn` options — empty with the rule off, for a
 *  child species with no table, or when a parent has no genome of that layout. The RNG
 *  is DERIVED from `her:breed` by (min id, max id, tick): it draws nothing from the
 *  world's stream, so the rule-off draw sequence is untouched. */
function inheritance(
  a: Entity, b: Entity, childType: string, ctx: MobContext,
): { genome?: Uint8Array; parents?: [number, number] } {
  if (!ctx.sys.heredityIsOn()) return {};
  const genes = ctx.sys.def(childType)?.genes;
  if (!genes || !a.genome || !b.genome || a.genome.length !== b.genome.length) return {};
  const rng = ctx.rng.derive(SALT_HER_BREED, Math.min(a.id, b.id), Math.max(a.id, b.id), ctx.frame.tick ?? 0);
  return { genome: recombine(genes, a.genome, b.genome, rng), parents: [a.id, b.id] };
}

/** A breeding timer, divided by the world's heredity pace for a table species under the
 *  rule (§28.8 — "at the points they are ASSIGNED"). Off, or a species with no table, the
 *  constant stands exactly and the pace is never even read. */
function timer(ticks: number, def: MobDef, ctx: MobContext): number {
  if (!def.genes || !ctx.sys.heredityIsOn()) return ticks;
  return paced(ticks, ctx.sys.heredityPace());
}
